import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../utils/api';
import { isHybridMode } from '../utils/config';

/**
 * Check if the logged-in user has match creation permissions (hybrid mode only).
 * Also fetches guild config (guild_id, features) and whether the user may manage
 * leagues (the ``league.manage`` web command).
 *
 * @returns {{ canCreate: boolean, canManageLeagues: boolean, guildId: string|null, features: object }}
 */
export default function usePermissions() {
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [canCreate, setCanCreate] = useState(false);
  const [canManageLeagues, setCanManageLeagues] = useState(false);
  const [guildId, setGuildId] = useState(null);
  const [features, setFeatures] = useState({});

  // Fetch config
  useEffect(() => {
    fetch(`${API_URL}/api/web/config`)
      .then(r => r.json())
      .then(data => {
        if (data?.guild?.guild_id) setGuildId(data.guild.guild_id);
        if (data?.features) setFeatures(data.features);
      })
      .catch(() => {});
  }, []);

  // Fetch permissions
  useEffect(() => {
    if (!isHybridMode || !isLoggedIn) {
      setCanCreate(false);
      setCanManageLeagues(false);
      return;
    }
    fetch(`${API_URL}/api/web/my-permissions`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const cmds = data?.commands || [];
        setCanCreate(data?.is_owner || cmds.includes('match.create'));
        setCanManageLeagues(data?.is_owner || cmds.includes('league.manage'));
      })
      .catch(() => {
        setCanCreate(false);
        setCanManageLeagues(false);
      });
  }, [isLoggedIn, getAuthHeaders]);

  return { canCreate, canManageLeagues, guildId, features };
}
